import React, { useState, useEffect, useMemo } from 'react';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  RefreshCw,
  UserCheck,
  Camera,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Briefcase,
  PlayCircle,
  FileCheck2,
  Star,
  ArrowRight,
} from 'lucide-react';
import { Job, Quote } from '../../types';

interface RealTimeJobStatusTrackerProps {
  job: Job;
  quotes?: Quote[];
  compact?: boolean;
  onRefresh?: () => void;
  onRateJob?: (job: Job) => void;
  className?: string;
}

export interface StageInfo {
  id: 'pending' | 'in_progress' | 'work_submitted' | 'completed';
  stageNumber: number;
  title: string;
  subtitle: string;
  description: string;
  status: 'completed' | 'current' | 'upcoming';
  timestamp?: string;
  badgeText?: string;
  badgeType?: 'neutral' | 'success' | 'warning' | 'info';
}

export const RealTimeJobStatusTracker: React.FC<RealTimeJobStatusTrackerProps> = ({
  job,
  quotes = [],
  compact = false,
  onRefresh,
  onRateJob,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(!compact);
  const [lastSyncedAt, setLastSyncedAt] = useState<string>('Just now');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  // 1-second tick for live server-synced arrival and ETA agreement countdowns
  useEffect(() => {
    const tick = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  // Auto-syncing interval: Auto-polls status changes every 10 seconds in real time
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (onRefresh) {
        onRefresh();
      }
      setLastSyncedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [onRefresh]);

  const handleManualSync = () => {
    setIsSyncing(true);
    if (onRefresh) {
      onRefresh();
    }
    setTimeout(() => {
      setLastSyncedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setIsSyncing(false);
    }, 800);
  };

  // Safe status extraction
  const rawStatus = String(job.status || '').toLowerCase();
  const rawCompStatus = String(job.completionStatus || (job as any).completion_status || '').toLowerCase();

  // Stage 4: Completed Work & Payment Disbursed
  // Evaluates customer release, payment disbursement, or final completed status
  const isPaidOrReleased = Boolean(
    job.payment_released === true ||
    (job as any).paymentReleased === true ||
    (job as any).payment_status === 'released' ||
    (job as any).paymentStatus === 'released' ||
    rawStatus === 'completed' ||
    rawCompStatus === 'confirmed' ||
    Boolean((job as any).confirmed_by_customer) ||
    Boolean((job as any).confirmedByCustomer) ||
    Boolean((job as any).customer_confirmed_at) ||
    Boolean(job.completed_by_customer_at)
  );

  // Stage 3: Work Complete with Photo Evidence
  // Evaluates professional completion mark and presence of inspection photo evidence
  const hasPhotoEvidence = Boolean(
    job.completionPhotoUrl ||
    (job as any).completion_photo_url ||
    job.completionPhotoPath ||
    (job as any).completion_photo_path ||
    (job as any).has_completion_photo === true ||
    (job as any).hasCompletionPhoto === true
  );

  const isProCompleted = Boolean(
    rawStatus === 'completed_by_professional' ||
    rawStatus === 'professional_completed' ||
    rawCompStatus === 'completed_by_professional' ||
    rawCompStatus === 'awaiting_customer_confirmation' ||
    (job as any).completed_by_professional === true ||
    (job as any).completedByProfessional === true ||
    Boolean(job.completed_by_pro_at) ||
    hasPhotoEvidence ||
    isPaidOrReleased
  );

  // Stage 2: Artisan Hired & Executing
  // Evaluates selected professional IDs (when not open), accepted quote statuses, and escrow payment tokens
  const hasAcceptedQuote = quotes.some(
    (q) => q.jobId === job.id && (q.status === 'accepted' || (q.status as string) === 'hired')
  );

  const hasPaymentTokens = Boolean(
    job.payment_reference ||
    (job as any).payment_reference ||
    (job as any).transaction_id ||
    (job as any).paymentReference ||
    (job as any).is_paid === true ||
    (job as any).isPaid === true ||
    (job as any).payment_status === 'held_in_escrow' ||
    (job as any).paymentStatus === 'held_in_escrow' ||
    (job as any).payment_status === 'paid' ||
    (job as any).paymentStatus === 'paid'
  );

  const hasAssignedPro = Boolean(
    job.selectedProfessionalId ||
    (job as any).selected_professional_id ||
    (job as any).professionalId ||
    (job as any).assignedProfessionalId
  );

  const isHiredOrInProgress = Boolean(
    (hasAssignedPro && rawStatus !== 'open') ||
    hasAcceptedQuote ||
    hasPaymentTokens ||
    rawStatus === 'in_progress' ||
    rawStatus === 'assigned' ||
    rawStatus === 'funded' ||
    rawStatus === 'active' ||
    isProCompleted ||
    isPaidOrReleased
  );

  // Determine active main stage
  let activeStageId: 'pending' | 'in_progress' | 'work_submitted' | 'completed' = 'pending';
  if (isPaidOrReleased) {
    activeStageId = 'completed';
  } else if (isProCompleted) {
    activeStageId = 'work_submitted';
  } else if (isHiredOrInProgress) {
    activeStageId = 'in_progress';
  } else {
    activeStageId = 'pending';
  }

  // Calculate percentage for animated progress bar
  let progressPercent = 25;
  if (activeStageId === 'pending') progressPercent = 25;
  if (activeStageId === 'in_progress') progressPercent = 50;
  if (activeStageId === 'work_submitted') progressPercent = 75;
  if (activeStageId === 'completed') progressPercent = 100;

  // Real-time quotes calculation for Stage 1
  const jobSpecificQuotes = quotes.filter((q) => q.jobId === job.id);
  const quotesCount = jobSpecificQuotes.length > 0 ? jobSpecificQuotes.length : (job.quotesCount || 0);

  // 4-Stage Definition
  const stages: StageInfo[] = [
    {
      id: 'pending',
      stageNumber: 1,
      title: 'Pending',
      subtitle: 'Job Request Posted',
      description: quotesCount > 0
        ? `${quotesCount} artisan quote${quotesCount > 1 ? 's' : ''} received. Compare quotes and select an artisan to hire.`
        : 'Active job request posted. Awaiting qualified artisan quotes in your LGA.',
      status: activeStageId === 'pending' ? 'current' : 'completed',
      timestamp: job.createdAt ? new Date(job.createdAt).toLocaleDateString() : undefined,
      badgeText: quotesCount > 0 ? `${quotesCount} Quote${quotesCount > 1 ? 's' : ''}` : 'Open Request',
      badgeType: quotesCount > 0 ? 'success' : 'neutral',
    },
    {
      id: 'in_progress',
      stageNumber: 2,
      title: 'Artisan Hired & Executing',
      subtitle: 'Selected & Escrow Secured',
      description: isHiredOrInProgress
        ? 'Professional artisan assigned. Escrow payment deposit secured & work execution underway.'
        : 'Select an artisan quote and complete escrow payment to advance to this stage.',
      status: activeStageId === 'in_progress'
        ? 'current'
        : activeStageId === 'work_submitted' || activeStageId === 'completed'
        ? 'completed'
        : 'upcoming',
      timestamp: (job as any).assigned_at || (job as any).hired_at ? new Date((job as any).assigned_at || (job as any).hired_at).toLocaleDateString() : undefined,
      badgeText: isHiredOrInProgress ? 'Artisan On Job' : 'Pending Hire',
      badgeType: isHiredOrInProgress ? 'info' : 'neutral',
    },
    {
      id: 'work_submitted',
      stageNumber: 3,
      title: 'Work Complete with Photo Evidence',
      subtitle: 'Inspection Photo Uploaded',
      description: isProCompleted
        ? 'Artisan completed service and submitted completion photo evidence for customer inspection.'
        : 'Artisan will snap & upload completion photo proof once work is finished.',
      status: activeStageId === 'work_submitted'
        ? 'current'
        : activeStageId === 'completed'
        ? 'completed'
        : 'upcoming',
      timestamp: (job as any).professional_completed_at || job.completedByProfessionalAt ? new Date((job as any).professional_completed_at || job.completedByProfessionalAt!).toLocaleDateString() : undefined,
      badgeText: isProCompleted ? 'Photo Proof Ready' : 'Awaiting Proof',
      badgeType: isProCompleted ? 'warning' : 'neutral',
    },
    {
      id: 'completed',
      stageNumber: 4,
      title: 'Completed Work & Payment Disbursed',
      subtitle: 'Payment Disbursed & Rated',
      description: isPaidOrReleased
        ? 'Work approved by customer. 90% payout disbursed to artisan & real-time rating unlocked.'
        : 'Customer inspection & confirmation releases 90% escrow payout to artisan.',
      status: activeStageId === 'completed' ? 'completed' : 'upcoming',
      timestamp: job.completed_at || job.completedByCustomerAt || (job as any).payment_released_at ? new Date(job.completed_at || job.completedByCustomerAt || (job as any).payment_released_at).toLocaleDateString() : undefined,
      badgeText: isPaidOrReleased ? '100% Resolved' : 'Final Step',
      badgeType: isPaidOrReleased ? 'success' : 'neutral',
    },
  ];

  const existingRating = Number((job as any).customer_rating || (job as any).aiScopeEstimate?.lifecycle_meta?.customer_rating || 0);

  return (
    <div className={`bg-white rounded-3xl border border-slate-200/90 shadow-xs p-4 sm:p-5 space-y-4 ${className}`}>
      {/* Header Bar with Live Sync & Direct Rating Trigger */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0 border border-emerald-200">
            {activeStageId === 'completed' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <PlayCircle className="w-4 h-4 text-emerald-600 animate-pulse" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xs sm:text-sm font-black text-slate-900">Real-Time Job Status Tracker</h4>
              {activeStageId === 'completed' ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black tracking-wider uppercase border border-emerald-300">
                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                  100% Completed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black tracking-wider uppercase border border-emerald-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  Live Sync (10s)
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500">
              Synced: <strong className="text-slate-700">{lastSyncedAt}</strong>
            </p>
          </div>
        </div>

        {/* Action Controls: Direct Rating Button & Manual Sync */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          {existingRating > 0 && (
            <span className="px-2.5 py-1.5 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 font-black text-xs flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              <span>Rated {existingRating}/5</span>
            </span>
          )}
          {activeStageId === 'completed' && onRateJob && (
            <button
              type="button"
              onClick={() => onRateJob(job)}
              className="px-3.5 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black text-xs shadow-xs flex items-center gap-1.5 cursor-pointer transition-all hover:scale-[1.02] active:scale-95 min-h-[36px]"
            >
              <Star className="w-3.5 h-3.5 fill-white" />
              <span>{existingRating > 0 ? 'Update Rating' : 'Rate Artisan ⭐'}</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleManualSync}
            disabled={isSyncing}
            className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1 text-xs font-semibold"
            title="Refresh Real-Time Status"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin text-emerald-600' : ''}`} />
            <span className="hidden sm:inline text-[11px]">Sync Now</span>
          </button>

          {compact && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
              title={isExpanded ? 'Collapse Tracker' : 'Expand Tracker'}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar Line */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 px-1">
          <span>Stage Progression ({progressPercent}%)</span>
          <span className="text-emerald-700 font-extrabold uppercase tracking-wider text-[11px]">
            {activeStageId === 'pending' && 'Stage 1: Pending (Job Request Posted)'}
            {activeStageId === 'in_progress' && 'Stage 2: Artisan Hired & Executing'}
            {activeStageId === 'work_submitted' && 'Stage 3: Work Complete with Photo Evidence'}
            {activeStageId === 'completed' && 'Stage 4: Completed Work & Payment Disbursed'}
          </span>
        </div>
        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200/60">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-emerald-500 to-emerald-600 transition-all duration-700 ease-out rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Live Server-Synced Arrival Countdown & 60m ETA Agreement Window */}
      {activeStageId === 'in_progress' && rawStatus !== 'cancelled' && rawStatus !== 'expired' && (() => {
        const formatCountdown = (remainingMs: number) => {
          const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
          const mins = Math.floor(totalSec / 60);
          const secs = totalSec % 60;
          return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        };

        const hasArrived = Boolean(
          job.professional_arrived ||
          job.professional_arrived_at ||
          job.customer_confirmed_arrival_at ||
          job.eta_status === 'arrived'
        );

        if (hasArrived) {
          const arrivedTime = job.customer_confirmed_arrival_at || job.professional_arrived_at;
          return (
            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-300 flex items-center justify-between gap-3 text-xs text-emerald-950">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div>
                  <span className="font-black block">Artisan Arrived On Site — Countdown Complete</span>
                  <span className="text-[11px] text-emerald-800">
                    {arrivedTime
                      ? `Confirmed at ${new Date(arrivedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT`
                      : 'On-site arrival verified'}
                  </span>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white font-black text-[10px] uppercase">
                Arrived ✓
              </span>
            </div>
          );
        }

        const agreedEtaIso = job.agreed_eta_at || job.scheduled_arrival_at;
        const graceMinutes = Number(job.grace_period_minutes ?? 15);
        const delayMinutes = Number(job.delay_minutes || 0);

        if (agreedEtaIso && !isNaN(new Date(agreedEtaIso).getTime())) {
          const agreedEtaMs = new Date(agreedEtaIso).getTime() + delayMinutes * 60000;
          const graceDeadlineMs = agreedEtaMs + graceMinutes * 60000;
          const remainingToEtaMs = agreedEtaMs - nowMs;
          const remainingGraceMs = graceDeadlineMs - nowMs;

          if (remainingToEtaMs > 0) {
            return (
              <div className="p-3 rounded-2xl bg-blue-50 border border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-blue-950">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600 shrink-0 animate-pulse" />
                  <div>
                    <span className="font-black block">Agreed Artisan Arrival Countdown</span>
                    <span className="text-[11px] text-blue-800">
                      Agreed ETA: {new Date(agreedEtaMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT ({graceMinutes}m grace period applies)
                    </span>
                  </div>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-blue-600 text-white font-mono font-black text-sm text-center shrink-0">
                  {formatCountdown(remainingToEtaMs)}
                </div>
              </div>
            );
          }

          if (remainingGraceMs > 0) {
            return (
              <div className="p-3 rounded-2xl bg-amber-50 border border-amber-300 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-amber-950">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 animate-bounce" />
                  <div>
                    <span className="font-black block">Arrival Overdue — {graceMinutes}-Minute Grace Period Active</span>
                    <span className="text-[11px] text-amber-800">
                      Confirm arrival if artisan is on site; otherwise auto-reassignment triggers when grace period ends.
                    </span>
                  </div>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-amber-600 text-white font-mono font-black text-sm text-center shrink-0">
                  Grace: {formatCountdown(remainingGraceMs)}
                </div>
              </div>
            );
          }

          return (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-300 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-rose-950">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <div>
                  <span className="font-black block">Arrival & {graceMinutes}-Minute Grace Period Expired</span>
                  <span className="text-[11px] text-rose-800">
                    No-show verification active — eligible for automatic reassignment or full escrow refund.
                  </span>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-rose-600 text-white font-black text-[10px] uppercase shrink-0">
                Overdue
              </span>
            </div>
          );
        }

        const agreementDeadlineIso =
          job.eta_agreement_deadline_at ||
          (job.accepted_at ? new Date(new Date(job.accepted_at).getTime() + 60 * 60000).toISOString() : null);

        if (agreementDeadlineIso && !isNaN(new Date(agreementDeadlineIso).getTime())) {
          const deadlineMs = new Date(agreementDeadlineIso).getTime();
          const remainingWindowMs = deadlineMs - nowMs;

          if (remainingWindowMs > 0) {
            return (
              <div className="p-3 rounded-2xl bg-emerald-50/90 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-emerald-950">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div>
                    <span className="font-black block">
                      {job.eta_status === 'proposed' && job.proposed_eta_minutes
                        ? `Proposed ETA: ~${job.proposed_eta_minutes} mins (Awaiting Customer Confirmation)`
                        : '60-Minute Arrival ETA Agreement Window'}
                    </span>
                    <span className="text-[11px] text-emerald-800">
                      Artisan must propose and customer must confirm arrival ETA within 60 mins of job acceptance.
                    </span>
                  </div>
                </div>
                <div className="px-3 py-1.5 rounded-xl bg-emerald-700 text-white font-mono font-black text-sm text-center shrink-0">
                  {formatCountdown(remainingWindowMs)}
                </div>
              </div>
            );
          }

          return (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-between gap-2 text-xs text-rose-950">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <div>
                  <span className="font-black block">60-Minute ETA Agreement Window Expired</span>
                  <span className="text-[11px] text-rose-800">
                    No arrival time was confirmed within 60 minutes. Job is eligible for reassignment or escrow refund.
                  </span>
                </div>
              </div>
            </div>
          );
        }

        return null;
      })()}

      {/* 4 Progression Steps Grid */}
      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 pt-1">
          {stages.map((stg) => {
            const isCurrent = stg.status === 'current';
            const isCompleted = stg.status === 'completed';
            const isFinalCompletedCard = stg.id === 'completed' && activeStageId === 'completed';

            return (
              <div
                key={stg.id}
                className={`p-3.5 rounded-2xl border transition-all space-y-2 relative flex flex-col justify-between ${
                  isCurrent || isFinalCompletedCard
                    ? 'bg-emerald-50/80 border-emerald-400 shadow-xs ring-2 ring-emerald-400/20'
                    : isCompleted
                    ? 'bg-slate-50 border-emerald-200/80 text-slate-800'
                    : 'bg-slate-50/50 border-slate-200 text-slate-400'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center shrink-0 ${
                          isCompleted
                            ? 'bg-emerald-600 text-white'
                            : isCurrent
                            ? 'bg-emerald-500 text-white animate-pulse'
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {isCompleted ? '✓' : stg.stageNumber}
                      </span>
                      <span
                        className={`text-xs font-black ${
                          isCurrent || isFinalCompletedCard
                            ? 'text-emerald-950'
                            : isCompleted
                            ? 'text-slate-900'
                            : 'text-slate-400'
                        }`}
                      >
                        {stg.title}
                      </span>
                    </div>

                    {stg.badgeText && (
                      <span
                        className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-md uppercase tracking-tight ${
                          stg.badgeType === 'success' || isFinalCompletedCard
                            ? 'bg-emerald-200 text-emerald-900'
                            : stg.badgeType === 'info' || (isCurrent && activeStageId === 'in_progress')
                            ? 'bg-blue-100 text-blue-900'
                            : stg.badgeType === 'warning' || (isCurrent && activeStageId === 'work_submitted')
                            ? 'bg-purple-100 text-purple-900'
                            : isCompleted
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-200 text-slate-500'
                        }`}
                      >
                        {stg.badgeText}
                      </span>
                    )}
                  </div>

                  <p className="text-[11px] font-bold text-slate-700 leading-tight">
                    {stg.subtitle}
                  </p>

                  <p className="text-[10px] text-slate-500 leading-normal">
                    {stg.description}
                  </p>
                </div>

                <div>
                  {/* Direct Rating Trigger inside Stage 4 Card */}
                  {stg.id === 'completed' && activeStageId === 'completed' && onRateJob && (
                    <button
                      type="button"
                      onClick={() => onRateJob(job)}
                      className="mt-2 w-full py-2 px-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs shadow-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all hover:scale-[1.01]"
                    >
                      <Star className="w-3.5 h-3.5 fill-white" />
                      <span>{existingRating > 0 ? `Rated ${existingRating}/5 • Edit` : 'Rate Artisan ⭐'}</span>
                    </button>
                  )}

                  {stg.timestamp && (
                    <div className="text-[9px] text-slate-400 font-mono pt-1.5 mt-2 border-t border-slate-200/60">
                      {stg.timestamp}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
